// ═══ Cinematic Demo Player — First-visit wrapper + walkthrough hand-off ═══
// Spec: 01 - Projects/Bianca Visualizer/Cinematic Demo Spec v1.md
//
// Mounts on first Studio visit. Plays CinematicDemo (36s) in a Remotion Player.
// At t=33s triggers driver.js walkthrough (task-015) — the cinematic and the
// walkthrough chain into one continuous onboarding moment.
// At t=36s unmounts. User is fully inside the Studio.

import { useEffect, useRef, useState, useCallback } from 'react'
import { Player, type PlayerRef } from '@remotion/player'
import { CinematicDemo, DEMO_DURATION_FRAMES, type CinematicDemoProps } from './CinematicDemo'

const DEMO_SEEN_KEY = 'studio-cinematic-demo-seen'
// Session flag — lets the next-mounted walkthrough know the cinematic just played
// and skip the generic welcome card ("don't undo Your Turn.").
const JUST_PLAYED_SESSION_KEY = 'studio-cinematic-just-played'
const DEMO_DURATION_S = 36.0

export function shouldShowCinematic(): boolean {
  try {
    return !localStorage.getItem(DEMO_SEEN_KEY)
  } catch {
    return false
  }
}

export function markCinematicSeen() {
  try {
    localStorage.setItem(DEMO_SEEN_KEY, 'true')
    // Session flag: read by the walkthrough on its next mount
    sessionStorage.setItem(JUST_PLAYED_SESSION_KEY, String(Date.now()))
  } catch {
    // storage unavailable — fail silent, demo just replays
  }
}

/** Read + consume the just-played session flag. Returns true once, then clears. */
export function consumeCinematicJustPlayed(maxAgeMs = 5 * 60 * 1000): boolean {
  try {
    const raw = sessionStorage.getItem(JUST_PLAYED_SESSION_KEY)
    if (!raw) return false
    sessionStorage.removeItem(JUST_PLAYED_SESSION_KEY)
    const ts = Number(raw)
    if (!Number.isFinite(ts)) return false
    return Date.now() - ts <= maxAgeMs
  } catch {
    return false
  }
}

export function resetCinematic() {
  try {
    localStorage.removeItem(DEMO_SEEN_KEY)
  } catch {
    // noop
  }
}

interface CinematicDemoPlayerProps {
  audioUrl?: string
  onComplete: () => void
  onSkip: () => void
  lyrics?: CinematicDemoProps['lyrics']
  prompt?: CinematicDemoProps['prompt']
}

export const CinematicDemoPlayer: React.FC<CinematicDemoPlayerProps> = ({
  audioUrl,
  onComplete,
  onSkip,
  lyrics,
  prompt,
}) => {
  const playerRef = useRef<PlayerRef>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [fadingOut, setFadingOut] = useState(false)

  // Poll player time — Remotion Player doesn't emit frame events, so we poll.
  useEffect(() => {
    const id = setInterval(() => {
      if (playerRef.current) {
        const frame = playerRef.current.getCurrentFrame()
        const t = frame / 30 // FPS
        setCurrentTime(t)

        // Begin fade-out at t=34.5s — UI emerges, demo dims
        if (t >= 34.5 && !fadingOut) {
          setFadingOut(true)
        }

        // End at t=36s
        if (t >= DEMO_DURATION_S) {
          markCinematicSeen()
          onComplete()
        }
      }
    }, 50)
    return () => clearInterval(id)
  }, [fadingOut, onComplete])

  const handleSkip = useCallback(() => {
    markCinematicSeen()
    onSkip()
  }, [onSkip])

  const inputProps: CinematicDemoProps = {
    audioUrl,
    lyrics,
    prompt,
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: '#05060A',
        opacity: fadingOut ? Math.max(0, 1 - (currentTime - 34.5) / 1.5) : 1,
        transition: 'opacity 0.1s linear',
        pointerEvents: fadingOut ? 'none' : 'auto',
      }}
    >
      <Player
        ref={playerRef}
        component={CinematicDemo}
        inputProps={inputProps}
        durationInFrames={DEMO_DURATION_FRAMES}
        compositionWidth={1920}
        compositionHeight={1080}
        fps={30}
        style={{ width: '100%', height: '100%' }}
        controls={false}
        autoPlay
        loop={false}
      />

      {/* Skip button (bottom-right, subtle) */}
      <button
        onClick={handleSkip}
        style={{
          position: 'absolute',
          bottom: 24,
          right: 24,
          padding: '10px 18px',
          background: 'rgba(0,0,0,0.4)',
          color: 'rgba(255,245,232,0.7)',
          border: '1px solid rgba(255,245,232,0.2)',
          borderRadius: 6,
          fontFamily: 'system-ui, sans-serif',
          fontSize: 13,
          cursor: 'pointer',
          letterSpacing: '0.03em',
          backdropFilter: 'blur(8px)',
        }}
        aria-label="Skip intro"
      >
        Skip intro
      </button>
    </div>
  )
}

export default CinematicDemoPlayer
